import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createAuthAdapter } from "./auth-adapter";
const AuthContext = createContext(undefined);
export const AuthProvider = ({ children }) => {
    const adapter = useMemo(() => createAuthAdapter(), []);
    // TODO: Should these be inline states? Or back to Zustand in the future?
    const [user, setUser] = useState(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(undefined);
    const [status, setStatus] = useState("uninitialized");
    useEffect(() => {
        let isMounted = true;
        // onAuthStateChange fires an INITIAL_SESSION event with the current user
        // on subscribe, so we don't need a separate getUser() call.
        //
        // Per-account cache clearing on user-id transitions lives in
        // `useAnnotations()` (mounted from app.tsx), which watches `user.id`
        // from this context and drops the local annotation collection when
        // the identity changes. Adding a per-feature cache reset here would
        // mean re-introducing per-feature imports into the auth context.
        const unsubscribe = adapter.subscribe((nextUser) => {
            if (!isMounted)
                return;
            setUser(nextUser);
            setStatus(nextUser ? "logged_in" : "logged_out");
            setIsLoading(false);
        });
        return () => {
            isMounted = false;
            unsubscribe();
        };
    }, [adapter]);
    const sendPhoneCode = useCallback(async (phoneNumber, password) => {
        setError(undefined);
        try {
            await adapter.sendPhoneCode(phoneNumber, password);
        }
        catch (err) {
            setError(err);
            throw err;
        }
    }, [adapter]);
    const verifyPhoneCode = useCallback(async (phoneNumber, password, code) => {
        setError(undefined);
        try {
            await adapter.verifyPhoneCode(phoneNumber, password, code);
        }
        catch (err) {
            setError(err);
            throw err;
        }
    }, [adapter]);
    const sendEmailCode = useCallback(async (email, password) => {
        setError(undefined);
        try {
            await adapter.sendEmailCode(email, password);
        }
        catch (err) {
            setError(err);
            throw err;
        }
    }, [adapter]);
    const verifyEmailCode = useCallback(async (email, password, code) => {
        setError(undefined);
        try {
            await adapter.verifyEmailCode(email, password, code);
        }
        catch (err) {
            setError(err);
            throw err;
        }
    }, [adapter]);
    const signOut = useCallback(async () => {
        setError(undefined);
        try {
            await adapter.signOut();
        }
        catch (err) {
            setError(err);
        }
    }, [adapter]);
    const getAccessToken = useCallback(async () => {
        try {
            return await adapter.getAccessToken();
        }
        catch (err) {
            setError(err);
            return undefined;
        }
    }, [adapter]);
    const refreshUser = useCallback(async () => {
        try {
            await adapter.refreshUser();
        }
        catch (err) {
            setError(err);
        }
    }, [adapter]);
    const value = {
        user,
        isLoading,
        status,
        error,
        sendPhoneCode,
        verifyPhoneCode,
        sendEmailCode,
        verifyEmailCode,
        signOut,
        getAccessToken,
        refreshUser,
    };
    return _jsx(AuthContext.Provider, { value: value, children: children });
};
export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return ctx;
};
